/**
 * Types of errors that can occur in the application
 */
export const ErrorType = {
  /** Network-related errors */
  NETWORK: 'NETWORK',
  /** Authentication errors */
  AUTH: 'AUTH',
  /** Database errors */
  DATABASE: 'DATABASE',
  /** Validation errors */
  VALIDATION: 'VALIDATION',
  /** Not found errors */
  NOT_FOUND: 'NOT_FOUND',
  /** Rate limit exceeded errors */
  RATE_LIMIT: 'RATE_LIMIT',
  /** Configuration errors */
  CONFIGURATION: 'CONFIGURATION',
  /** Conflict errors (e.g., trying to delete a referenced resource) */
  CONFLICT: 'CONFLICT',
  /** Generic AI Service errors */
  AI_SERVICE_ERROR: 'AI_SERVICE_ERROR',
  /** Specific AI Rate Limit errors */
  RATE_LIMIT_ERROR: 'RATE_LIMIT_ERROR',
  /** Specific AI Authentication errors */
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  /** Specific AI Service Unavailable errors */
  AI_SERVICE_UNAVAILABLE: 'AI_SERVICE_UNAVAILABLE',
  /** Unexpected errors */
  UNEXPECTED: 'UNEXPECTED',
  /** Unknown errors */
  UNKNOWN: 'UNKNOWN'
} as const;

// Derive the type from the object keys
export type ErrorType = typeof ErrorType[keyof typeof ErrorType];

/**
 * Options for constructing an AppError
 */
export interface AppErrorOptions {
  /** Error message */
  message: string;
  /** Type of error */
  type?: ErrorType;
  /** Original error that caused this error */
  cause?: any;
  /** HTTP status code if applicable */
  statusCode?: number;
  /** Additional context for the error */
  metadata?: Record<string, any>;
}

/**
 * Application error with additional context
 */
export class AppError extends Error {
  /** Type of error */
  type: ErrorType;
  /** Original error that caused this error */
  originalError?: any;
  /** HTTP status code if applicable */
  statusCode?: number;
  /** Additional context for the error */
  context?: Record<string, any>;
  /** Additional metadata for the error */
  metadata?: Record<string, any>;

  constructor(
    messageOrOptions: string | AppErrorOptions,
    type: ErrorType = ErrorType.UNKNOWN,
    originalError?: any,
    statusCode?: number,
    context?: Record<string, any>
  ) {
    if (typeof messageOrOptions === 'string') {
      super(messageOrOptions);
      this.type = type;
      this.originalError = originalError;
      this.statusCode = statusCode;
      this.context = context;
    } else {
      super(messageOrOptions.message);
      this.type = messageOrOptions.type || ErrorType.UNKNOWN;
      this.originalError = messageOrOptions.cause;
      this.statusCode = messageOrOptions.statusCode;
      this.context = messageOrOptions.metadata;
      this.metadata = messageOrOptions.metadata;
    }
    this.name = 'AppError';
  }

  /**
   * Returns a string representation of the error
   */
  toString(): string {
    return `${this.name}[${this.type}]: ${this.message}${
      this.metadata ? ` (metadata: ${JSON.stringify(this.metadata)})` : ''
    }`;
  }
}

/**
 * Classifies a Supabase error into an appropriate ErrorType
 */
export function classifySupabaseError(error: any): ErrorType {
  if (!error) return ErrorType.UNKNOWN;

  // Check for network errors
  if (
    error.message?.includes('network') ||
    error.message?.includes('timeout') ||
    error.message?.includes('connection')
  ) {
    return ErrorType.NETWORK;
  }

  // Check for authentication errors
  if (
    error.code === 'auth/invalid-email' ||
    error.code === 'auth/user-not-found' ||
    error.code === 'auth/wrong-password' ||
    error.message?.includes('auth') ||
    error.message?.includes('authentication') ||
    error.message?.includes('permission') ||
    error.message?.includes('not authorized') ||
    error.status === 401 ||
    error.status === 403
  ) {
    return ErrorType.AUTH;
  }

  // Check for not found errors
  if (
    error.code === 'PGRST116' ||
    error.message?.includes('not found') ||
    error.message?.includes('does not exist') ||
    error.status === 404
  ) {
    return ErrorType.NOT_FOUND;
  }

  // Check for validation errors
  if (
    error.code?.startsWith('22') || // PostgreSQL error codes for data exceptions
    error.code === '23505' || // Unique violation
    error.message?.includes('validation') ||
    error.message?.includes('constraint') ||
    error.message?.includes('invalid') ||
    error.status === 400 ||
    error.status === 422
  ) {
    return ErrorType.VALIDATION;
  }

  // Check for database errors
  if (
    error.code?.startsWith('23') || // PostgreSQL error codes for integrity constraint violations
    error.code?.startsWith('25') || // PostgreSQL error codes for invalid transaction state
    error.code?.startsWith('40') || // PostgreSQL error codes for transaction rollback
    error.code?.startsWith('53') || // PostgreSQL error codes for insufficient resources
    error.code?.startsWith('55') || // PostgreSQL error codes for object not in prerequisite state
    error.message?.includes('database') ||
    error.message?.includes('relation') ||
    error.message?.includes('column') ||
    error.status === 500
  ) {
    return ErrorType.DATABASE;
  }

  return ErrorType.UNKNOWN;
}

/**
 * Handles an error by classifying it and creating an AppError
 */
export function handleError(
  error: any,
  operation: string,
  context?: Record<string, any>
): AppError {
  // If it's already an AppError, just return it
  // Use duck-typing instead of instanceof for reliability in test environments
  if (error && 
      typeof error === 'object' && 
      typeof (error as any).type === 'string' && 
      Object.values(ErrorType).includes((error as any).type)) {
    return error as AppError; // Return the existing AppError, preserving its type
  }

  // Classify the error
  const errorType = classifySupabaseError(error);
  
  // Create a more descriptive message
  let message = `Error during ${operation}: ${error.message || 'Unknown error'}`;
  
  // Extract status code if available
  const statusCode = error.status || error.statusCode || undefined;
  
  // Create and return an AppError
  return new AppError(message, errorType, error, statusCode, context);
}

/**
 * Logs an error with appropriate detail level
 */
export function logError(error: any, operation: string): void {
  const appError = error instanceof AppError 
    ? error 
    : handleError(error, operation); // Ensure context from handleError call is included if wrapping
  
  // Log basic error info
  console.error(`[${appError.type}] Error during ${operation}: ${appError.message}`);
  
  // Log Status Code if available
  if (appError.statusCode) {
    console.error(`  Status Code: ${appError.statusCode}`);
  }

  // --- MODIFIED: Selectively log context/metadata --- 
  const logContext = (label: string, data: Record<string, any> | undefined) => {
    if (!data) return;
    // Define keys considered potentially sensitive or too large
    const sensitiveKeys = ['requestBody', 'responseBody', 'poemContent', 'content', 'style_points', 'options'];
    const keysToLog: Record<string, any> = {};
    let hasSensitive = false;

    for (const key in data) {
      if (sensitiveKeys.includes(key)) {
        // Log presence of key but not its full value
        keysToLog[key] = `[${typeof data[key]} present - value omitted]`; 
        hasSensitive = true;
      } else {
        // Log non-sensitive values directly
        keysToLog[key] = data[key]; 
      }
    }
    console.error(`  ${label}:`, JSON.stringify(keysToLog, null, 2));
    // if (hasSensitive) {
    //   console.error(`    (Full sensitive data omitted from log)`);
    // }
  };

  logContext('Context', appError.context);
  // Only log metadata if it's different from context (AppError constructor sets both initially)
  if (appError.metadata && JSON.stringify(appError.metadata) !== JSON.stringify(appError.context)) {
    logContext('Metadata', appError.metadata);
  }
  // --- END MODIFICATION ---
  
  // Log original error stack if different and available
  const original = appError.originalError || error;
  if (original && original !== appError) { 
    console.error('  Original error:', 
      original instanceof Error 
        ? original.stack || original.message
        : JSON.stringify(original, null, 2)
    );
  }
  
  // In a production environment, you might want to send this to a logging service
  // sendToLoggingService(appError);
} 